import { useEffect, useState } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { ProgressBarWizard } from '@bambi/component-library/src/components/Wizard/ProgressBarWizard';

import {
  PricingSchemeRead,
  usePricingPricingSchemesRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';

import { PricingWizardSchemeConfigurationStep } from './PricingWizardSchemeConfigurationStep';
import {
  PricingWizardSchemeMetaStep,
  SchemeMetaFormValues,
} from './PricingWizardSchemeMetaStep';

export type PricingWizardMode = 'create' | 'edit' | 'clone';
export function PricingWizard({
  pricingSchemeId,
  onCancel,
  onSuccess,
  mode = 'create',
}: {
  pricingSchemeId?: string;
  onCancel: () => void;
  onSuccess: () => void;
  mode: PricingWizardMode;
}) {
  const [workInProgressScheme, setWorkInProgressScheme] = useState<
    SchemeMetaFormValues | undefined
  >();

  const [cloneEditData, setCloneEditData] = useState<
    PricingSchemeRead | undefined
  >();

  const { data, isLoading } = usePricingPricingSchemesRetrieveQuery(
    {
      id: pricingSchemeId || '',
    },
    {
      skip: mode === 'create' || !pricingSchemeId,
    }
  );

  const createCloneData = (
    originalData: PricingSchemeRead
  ): PricingSchemeRead => ({
    ...originalData,
    id: '',
    name: `Copy of ${originalData.name}`,
  });

  useEffect(() => {
    if (mode === 'clone' && data) {
      setCloneEditData(createCloneData(data));
    } else if (mode === 'edit') {
      setCloneEditData(data);
    }
  }, [mode, data]);

  const isDataLoaded = (): boolean => {
    return Boolean(cloneEditData?.space_types?.length);
  };

  if (isLoading || (mode !== 'create' && !isDataLoaded())) {
    return <LoadingIndicator />;
  }

  return (
    <ProgressBarWizard
      steps={[
        {
          title: 'Base Configuration',
          render: ({ next }) => (
            <PricingWizardSchemeMetaStep
              originalPricingSchemeValues={cloneEditData}
              initialValues={workInProgressScheme}
              onCancel={() => {
                onCancel();
              }}
              onSubmit={(values) => {
                setWorkInProgressScheme(values);
                next();
              }}
            />
          ),
        },
        {
          title: `${workInProgressScheme?.name}`,
          render: () => (
            <PricingWizardSchemeConfigurationStep
              originalPricingSchemeValues={cloneEditData}
              onCancel={() => {
                onCancel();
              }}
              onSubmit={() => {
                onSuccess();
              }}
              workInProgressScheme={
                workInProgressScheme as SchemeMetaFormValues
              }
              mode={mode}
            />
          ),
        },
      ]}
    />
  );
}
